import { styled, Grid, Button } from '@ori-ui/mui';
import type { GlobalCustomerRegistrationsRegistrationPersonalDetailsDto } from '@ori/global-api-client-react';
import type { StaticCustomerRegistrationsMetadataRegistrationPersonalDetailsMetadataDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';

import { Localization, GenderLabelMapper } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useFormError } from '../../../contexts';
import { useTranslations } from '../../../hooks/useTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../../models';
import {
  DatePickerField,
  PhoneFieldByConfig,
  SelectFormFieldByConfig,
  TextFormFieldByConfig,
} from '../../FieldsByConfig';
import { FormValidationSummary } from '../../FormValidationSummary';
import { AddressSection } from './AddressSection';

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(8),
}));

const SubmitButtton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(3),
  order: 20,
}));

interface IPersonalDetailsFormProps {
  initialData?: GlobalCustomerRegistrationsRegistrationPersonalDetailsDto;
  config?: StaticCustomerRegistrationsMetadataRegistrationPersonalDetailsMetadataDto;
  isSuccessSubmit: boolean;
  onSubmitForm: (data: GlobalCustomerRegistrationsRegistrationPersonalDetailsDto) => void;
}

export const PersonalDetailsForm: FC<IPersonalDetailsFormProps> = (props) => {
  const { initialData, config, isSuccessSubmit, onSubmitForm } = props;

  const { formatMessage } = useTranslations();

  const { customerServiceUrl } = useConfiguration();

  const { getTestIdProps } = useTestId();

  const datePickerPlaceholder = formatMessage(TranslationKey.FirstStep_PlaceHolder);

  const methods = useForm<GlobalCustomerRegistrationsRegistrationPersonalDetailsDto>({
    defaultValues: initialData,
    reValidateMode: 'onChange',
    mode: 'onBlur',
  });

  const { formState, watch } = methods;
  const { isSubmitting } = formState;

  const formValues = watch();

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const invalidCodeErrors = customErrors?.filter(
    (er) =>
      !Object.keys(formValues)
        .map((val) => val.toLowerCase())
        .includes(er.fieldName?.toLowerCase() ?? ''),
  );

  return (
    <FormProvider {...methods}>
      <FormValidationSummary
        invalidPropertyErrors={invalidCodeErrors}
        customerServiceUrl={customerServiceUrl}
      />
      <StyledForm
        noValidate
        onSubmit={methods.handleSubmit(onSubmitForm)}
      >
        <Grid
          container
          justifyContent="center"
          order={1}
        >
          <TextFormFieldByConfig
            name="firstName"
            label={
              <Localization
                id={TranslationKey.FirstStep_FirstName}
                defaultMessage={defaultTranslations.FirstStep_FirstName}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.FirstStep_FirstName_Tooltip}
                defaultMessage={defaultTranslations.FirstStep_FirstName_Tooltip}
              />
            }
            {...config?.firstName}
            useUpperCase={config?.useUpperCase?.isEnabled}
          />
          <TextFormFieldByConfig
            enableLowercaseLetters
            name="contactEmail"
            label={
              config?.contactEmail?.validation?.isMandatory ? (
                <Localization
                  id={TranslationKey.FirstStep_Email}
                  defaultMessage={defaultTranslations.FirstStep_Email}
                />
              ) : (
                <Localization
                  id={TranslationKey.FirstStep_Email_Optional}
                  defaultMessage={defaultTranslations.FirstStep_Email_Optional}
                />
              )
            }
            tooltipText={
              <Localization
                id={TranslationKey.FirstStep_TooltipEmail}
                defaultMessage={defaultTranslations.FirstStep_TooltipEmail}
              />
            }
            customPlaceholder={formatMessage(TranslationKey.FirstStep_PlaceholderEmail)}
            keyboardType="email"
            {...config?.contactEmail}
          />
          <PhoneFieldByConfig
            name="contactTelephone"
            label={
              config?.contactTelephone?.validation?.isMandatory ? (
                <Localization
                  id={TranslationKey.FirstStep_Phone}
                  defaultMessage={defaultTranslations.FirstStep_Phone}
                />
              ) : (
                <Localization
                  id={TranslationKey.FirstStep_Phone_Optional}
                  defaultMessage={defaultTranslations.FirstStep_Phone_Optional}
                />
              )
            }
            labelWithPrefix={
              <Localization
                id={TranslationKey.FirstStep_PhoneWithPrefix}
                defaultMessage={defaultTranslations.FirstStep_PhoneWithPrefix}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.FirstStep_TooltipPhone}
                defaultMessage={defaultTranslations.FirstStep_TooltipPhone}
              />
            }
            tooltipWithPrefix={
              <Localization
                id={TranslationKey.FirstStep_TooltipPhoneWithPrefix}
                defaultMessage={defaultTranslations.FirstStep_TooltipPhoneWithPrefix}
              />
            }
            customPlaceholder={formatMessage(TranslationKey.FirstStep_PlaceholderPhone)}
            customPlaceholderWithPrefix={formatMessage(TranslationKey.FirstStep_PlaceholderPhoneWithPrefix)}
            initialValue={formValues.contactTelephone}
            {...config?.contactTelephone}
          />
          {config?.address?.operations?.read?.isEnabled ? (
            <AddressSection
              configAddress={config.address}
              actualValues={formValues}
              useUpperCaseAddress={config.useUpperCase?.isEnabled}
            />
          ) : (
            <SelectFormFieldByConfig
              key={formValues.address?.country}
              name="address.country"
              label={
                <Localization
                  id={TranslationKey.FirstStep_Country}
                  defaultMessage={defaultTranslations.FirstStep_Country}
                />
              }
              placeholder={
                <Localization
                  id={TranslationKey.FirstStep_Country_Placeholder}
                  defaultMessage={defaultTranslations.FirstStep_Country_Placeholder}
                />
              }
              tooltipText={
                <Localization
                  id={TranslationKey.FirstStep_Country_Tooltip}
                  defaultMessage={defaultTranslations.FirstStep_Country_Tooltip}
                />
              }
              {...config?.address?.country}
            />
          )}

          <TextFormFieldByConfig
            name="lastName"
            label={
              <Localization
                id={TranslationKey.FirstStep_LastName}
                defaultMessage={defaultTranslations.FirstStep_LastName}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.FirstStep_LastName_Tooltip}
                defaultMessage={defaultTranslations.FirstStep_LastName_Tooltip}
              />
            }
            {...config?.lastName}
            useUpperCase={config?.useUpperCase?.isEnabled}
          />
          <TextFormFieldByConfig
            name="uniqueId"
            label={
              <Localization
                id={TranslationKey.FirstStep_UniqueId}
                defaultMessage={defaultTranslations.FirstStep_UniqueId}
              />
            }
            tooltipText={
              <Localization
                id={TranslationKey.FirstStep_UniqueId_Tooltip}
                defaultMessage={defaultTranslations.FirstStep_UniqueId_Tooltip}
              />
            }
            {...config?.uniqueId}
          />
          <SelectFormFieldByConfig
            name="gender"
            label={
              <Localization
                id={TranslationKey.FirstStep_Gender}
                defaultMessage={defaultTranslations.FirstStep_Gender}
              />
            }
            labelMapper={GenderLabelMapper}
            placeholder={
              <Localization
                id={TranslationKey.FirstStep_Gender_Placeholder}
                defaultMessage={defaultTranslations.FirstStep_Gender_Placeholder}
              />
            }
            {...config?.gender}
          />
          <DatePickerField
            name="birthDate"
            label={
              <Localization
                id={TranslationKey.FirstStep_DateOfBirth}
                defaultMessage={defaultTranslations.FirstStep_DateOfBirth}
              />
            }
            infoText={
              <Localization
                id={TranslationKey.FirstStep_DateOfBirthText}
                defaultMessage={defaultTranslations.FirstStep_DateOfBirthText}
              />
            }
            placeholderText={datePickerPlaceholder}
            invalidMsg={TranslationKey.validationBirthDateRegex}
            {...config?.birthDate}
          />
          <SubmitButtton
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting || isSuccessSubmit}
            {...getTestIdProps('submit-btn-personalForm')}
          >
            <Localization
              id={TranslationKey.FirstStep_Button}
              defaultMessage={defaultTranslations.FirstStep_Button}
            />
          </SubmitButtton>
        </Grid>
      </StyledForm>
    </FormProvider>
  );
};
